// University of St. Thomas (Minnesota): public college page (#2748), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - St. Thomas posts no Common Data Set (its Institutional Effectiveness
//    census reports sit on a sign-in-only SharePoint site, and no CDS file is
//    on stthomas.edu). The first-year counts come from the U.S. Department of
//    Education's IPEDS Admissions survey for Fall 2024, first-time
//    degree-seeking students (College Navigator, unit 174914: 9,774
//    applicants, 85% admitted, 21% of admits enrolled; the counts of 8,343
//    admitted and 1,737 enrolled are from the ADM2024 complete data file for
//    the same unit, and match those percentages).
//  - St. Thomas Admissions: First-Year Application Process ("Applications
//    for fall 2027 are open") and Test Optional. Neither page publishes
//    decision dates.
//  - The undergraduate catalog's Music page (the audition for music majors).
//  - Financial Aid, Cost of Attendance for Undergraduate Students, 2026-27.
//    Its items add up to its own total ($77,772).
//  - College Scorecard, school id 174914 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=174914#admsns',
        read: '2026-10-01',
    },
    apply: {
        label: 'St. Thomas Admissions, First-Year Application Process',
        url: 'https://www.stthomas.edu/admissions/undergraduate/first-year/application-process/',
        read: '2026-10-01',
    },
    tests: {
        label: 'St. Thomas Admissions, Test Optional',
        url: 'https://www.stthomas.edu/admissions/undergraduate/first-year/test-optional/',
        read: '2026-10-01',
    },
    music: {
        label: 'St. Thomas Undergraduate Catalog, Music',
        url: 'https://www.stthomas.edu/catalog/curricula/music/index.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'St. Thomas Financial Aid, Cost of Attendance for Undergraduate Students',
        url: 'https://www.stthomas.edu/costs/undergraduate/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?174914-University-of-St-Thomas',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-st-thomas-minnesota',
    kbId: 'university_of_st_thomas_minnesota',
    name: 'University of St. Thomas, Minnesota',
    shortName: 'St. Thomas',
    aliases: ['UST'],
    location: 'St. Paul, MN',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Tommie athletics brand standards); decoration only.
    branding: {
        primary: '#512773',
        secondary: '#98999B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_st_thomas_minnesota_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 9774,
        admitted: 8343,
        enrolled: 1737,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'St. Thomas posts no Common Data Set and publishes no first-year acceptance rate by college or major; the federal admissions survey reports one rate for the whole university.',
        source: 'ipeds',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to St. Thomas as a whole, with the same application for every major. One major asks for more:',
        rows: [
            { school: 'Music', requirement: 'Students who intend to major in music must pass a performance or portfolio audition.', source: 'music' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: null, binding: false },
            { plan: 'After January 15', deadline: 'Rolling', decision: null, binding: false },
        ],
        note: 'Both plans are free and not binding, so you do not commit to St. Thomas until May 1. Review starts in October; after January 15, applications are reviewed on a rolling basis. St. Thomas does not publish decision dates. A transcript is required; the essay and letters of recommendation are optional.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'St. Thomas is test-optional: on the application you choose to apply "with test" or "without test." Without test, your scores are not considered even if they were sent. With test, you report your highest composite and section scores on the application, and St. Thomas looks at the highest of each. A low score, including a section score, may count against you, and once submitted it cannot be removed. Merit scholarships are considered either way.',
        source: 'tests',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 56856 },
            { item: 'Required fees', amount: 1742 },
            { item: 'Housing (average on-campus room)', amount: 9812 },
            { item: 'Food', amount: 5972 },
            { item: 'Books, supplies, course materials and equipment', amount: 1000 },
            { item: 'Transportation', amount: 868 },
            { item: 'Personal, loan fees and miscellaneous expenses', amount: 1522 },
        ],
        total: 77772,
        note: 'St. Thomas\'s estimated cost of attendance for a full-time undergraduate. Housing is the average on-campus room. Nursing students pay higher fees.',
        source: 'costs',
        netPrice: {
            average: 31532,
            byIncome: [
                { income: '$0 to $30,000', amount: 29201 },
                { income: '$30,001 to $48,000', amount: 24697 },
                { income: '$48,001 to $75,000', amount: 26753 },
                { income: '$75,001 to $110,000', amount: 28875 },
                { income: 'Over $110,000', amount: 36792 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from St. Thomas\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
